import { ReactEventHandler, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import moment from "moment";

import { getMemberRelationshipLabelsToStudent } from "./relationship-utils";
import { useFamilyConstants, useFamilyMembers, type FamilyMemberInfo } from "../../hooks/use-families";
import {
  InquiryAppointment,
  useCreateOrUpdateInquiry,
  useInquiryAppointments,
  useInquiryDashboard,
  useInquiryFamilyInfo,
  type InquiryFormData
} from "../../hooks/use-inquiries";
import { SimpleTable, SimpleTableHeader } from "../table";
import { ContentPad, Utility } from "../../app/app-layout";
import { useAppointmentFormModal } from "../../forms/appointment/appointment-form";
import { useAccessControl } from "../../app/use-access-control";
import { UpcomingEventsTable } from "../inquiry-list/tab-content/inquiry-dashboard";
import { useAppointmentSummaryModal } from "../inquiry-list/tab-content/inquiry-dashboard/appointment-summary";
import { AppointmentHistoryAccordion } from "../inquiry-list/tab-content/inquiry-dashboard/appointment-history-accordion";
import { InitialContact } from "../inquiry-list/tab-content/inquiry-dashboard/initial-contact";
import { Confirm, ConfirmHandle } from "../confirm/confirm";

export interface InquiryRightSideAccordionProps {
  familyId?: string | null;
  onSaveInquiry: (savedInquiry: InquiryFormData) => void;
  onClose: () => void;
}

export interface RelationshipsItem {
  subjectContactId: number;
  subjectDisplayName?: string | undefined;
  relationship: string;
}

export const InquiryRightSideAccordion: React.FC<InquiryRightSideAccordionProps> = ({
  familyId,
  onSaveInquiry,
  onClose
}) => {
  const { familyRelationshipLookup, EMERGENCY_CONTACT_VALUE } = useFamilyConstants();
  const { data: allFamilyMembers } = useFamilyMembers(familyId);
  const inquiryForm = useFormContext<InquiryFormData>();
  const { watch, formState } = inquiryForm;
  const { activeCenterId } = useAccessControl();
  const createOrUpdateInquiryMutator = useCreateOrUpdateInquiry();

  const student = watch("student");
  const inquirer = watch("inquirer");
  const relationshipId = watch("relationshipId");
  const inquiryId = watch("id");
  const { members: inquiryFamilyMembers } = useInquiryFamilyInfo({
    centerId: activeCenterId,
    inquiryId: inquiryId ?? ""
  });

  const scopedFamilyMembers = useMemo(() => {
    if (inquiryId) {
      return inquiryFamilyMembers;
    }

    return allFamilyMembers?.members ?? [];
  }, [inquiryId, inquiryFamilyMembers, allFamilyMembers?.members]);

  const relationships: RelationshipsItem[] = useMemo(() => {
    const results = new Map<number, RelationshipsItem>();
    const sortedMembers = scopedFamilyMembers
      .slice()
      .sort((left, right) => left.displayName.localeCompare(right.displayName));

    sortedMembers.forEach(member => {
      const relationshipLabels =
        member.id === student?.id
          ? ["Student"]
          : getMemberRelationshipLabelsToStudent({
              member: member as FamilyMemberInfo,
              studentId: student?.id,
              emergencyContactRelationshipId: EMERGENCY_CONTACT_VALUE,
              familyRelationshipLookup
            });

      results.set(member.id, {
        subjectDisplayName: member.displayName,
        subjectContactId: member.id,
        relationship: relationshipLabels.join(", ")
      });
    });

    if (inquirer && !results.has(inquirer.id)) {
      results.set(inquirer.id, {
        subjectDisplayName: inquirer.displayName,
        subjectContactId: inquirer.id,
        relationship: familyRelationshipLookup[relationshipId ?? ""] ?? ""
      });
    }

    return Array.from(results.values()).sort((left, right) =>
      (left.subjectDisplayName ?? "").localeCompare(right.subjectDisplayName ?? "")
    );
  }, [scopedFamilyMembers, student?.id, inquirer, relationshipId, EMERGENCY_CONTACT_VALUE, familyRelationshipLookup]);

  const familyDetailHeaders: SimpleTableHeader<RelationshipsItem>[] = [
    {
      key: "subjectDisplayName",
      name: "Name"
    },
    {
      key: "relationship",
      name: "Relationship"
    }
  ];

  const [selectedUpcomingAppointment, setSelectedUpcomingAppointment] = useState<InquiryAppointment>();
  const [activeAppointment, setActiveAppointment] = useState<InquiryAppointment>();

  const dashboardQuery = useInquiryDashboard({
    centerId: activeCenterId,
    inquiryId: inquiryId
  });

  const { updateEventMutator } = useInquiryAppointments({
    centerId: activeCenterId,
    inquiryId: inquiryId ?? ""
  });

  const { showAppointmentFormModal, renderAppointmentFormModal } = useAppointmentFormModal({
    inquiryId: inquiryId ?? "",
    appointment: activeAppointment,
    centerId: activeCenterId,
    attendees: dashboardQuery.data?.attendees ?? []
  });

  const handleUpdateAppointmentStatus = (appointment: InquiryAppointment) => {
    updateEventMutator.mutate({
      ...appointment,
      scheduledDateTime: moment.utc(appointment.scheduledDateTime).format("YYYY-MM-DDTHH:mm:ssZ"),
      ...(appointment.endDateTime && {
        endDateTime: moment.utc(appointment.endDateTime).format("YYYY-MM-DDTHH:mm:ssZ")
      }),
      staffMembers: appointment.staffMembers.map(({ id }) => id).filter(id => id),
      attendees: appointment.attendees.map(({ id }) => id)
    });
  };

  const handleRescheduleAppointment = (appointment: InquiryAppointment) => {
    setActiveAppointment(appointment);
    showAppointmentFormModal();
  };

  const confirmSaveInquiry = useRef<ConfirmHandle>(null);

  const handleCreateNewAppointment: ReactEventHandler<HTMLButtonElement> = event => {
    (event.currentTarget as HTMLElement).blur();

    if (!familyId || !inquiryId || formState.isDirty) {
      confirmSaveInquiry.current?.confirm(() => {
        inquiryForm.handleSubmit(formValues => {
          createOrUpdateInquiryMutator.mutate(
            { inquiryId: formValues.id, formData: formValues },
            {
              onSuccess: savedInquiry => {
                onSaveInquiry(savedInquiry);
                openCreateAppointmentModal();
              }
            }
          );
        })(event);
      });
    } else {
      openCreateAppointmentModal();
    }
  };

  const openCreateAppointmentModal = () => {
    setActiveAppointment(undefined);
    showAppointmentFormModal();
  };

  const handleClickUpcomingAppointment = (appointment: InquiryAppointment) => {
    setSelectedUpcomingAppointment(appointment);
    showAppointmentSummaryModal();
  };

  const { showAppointmentSummaryModal, renderAppointmentSummaryModal } = useAppointmentSummaryModal({
    student: dashboardQuery.data?.student,
    familyInfo: dashboardQuery.data?.familyInfo,
    contacts: dashboardQuery.data?.attendees,
    appointment: selectedUpcomingAppointment,
    onUpdateAppointmentStatus: handleUpdateAppointmentStatus,
    onRescheduleAppointment: handleRescheduleAppointment
  });

  return (
    <>
      <Confirm
        ref={confirmSaveInquiry}
        title="Confirm"
        message="The inquiry MUST be saved before you can create an appointment. Would you like to save this inquiry?"
        okLabel="Save"
        cancelLabel="Cancel"
      />
      <div className="sticky-top d-lg-none">
        <Utility>
          <div className="d-flex stack-sm-down w-100 justify-content-end">
            <button className="btn btn-secondary me-2" onClick={onClose}>
              Exit
            </button>
          </div>
        </Utility>
      </div>
      <ContentPad>
        <h2 className="header-small">Details</h2>

        {/* this uses the OG accordion CSS classes because the react-bootstrap components refuse to 
        cooperate with the secondary button in the appointments header */}
        <div className="accordion">
          <div className="accordion-item">
            <h3 className="accordion-header" id="initialContactHeader">
              <button
                className="accordion-button"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#initialContactForm"
                aria-expanded="true"
                aria-controls="collapseLayout"
              >
                Initial Contact
              </button>
            </h3>
            <div
              id="initialContactForm"
              className="accordion-collapse collapse show"
              aria-labelledby="initialContactHeader"
            >
              <div className="accordion-body">
                <div className="card mb-0">
                  <div className="card-body pt-0">
                    <InitialContact inquiryForm={inquiryForm} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="accordion-item">
            <h3 className="accordion-header" id="contactRelationshipsHeader">
              <button
                className="accordion-button"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#contactRelationships"
                aria-expanded="true"
                aria-controls="collapseLayout"
              >
                Family Details
              </button>
            </h3>
            <div
              id="contactRelationships"
              className="accordion-collapse collapse show"
              aria-labelledby="contactRelationshipsHeader"
            >
              <div className="accordion-body">
                <SimpleTable
                  tableCollapseScreenSize="sm"
                  headers={familyDetailHeaders}
                  data={relationships}
                  uniqueIdKey="subjectContactId"
                />
              </div>
            </div>
          </div>
          <div className="accordion-item  has-secondary-header-button">
            <h3 className="accordion-header" id="upcomingAppointmentsHeader">
              <button
                className="accordion-button"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#upcomingAppointments"
                aria-expanded="true"
                aria-controls="collapseLayout"
              >
                Upcoming Appointments
              </button>
              <OverlayTrigger
                trigger={!familyId || !inquiryId || formState.isDirty ? ["focus", "hover", "click"] : []}
                placement={"top"}
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Inquiry must be saved before appointments can be added</Tooltip>}
              >
                <button className="btn btn-primary secondary-header-button" onClick={handleCreateNewAppointment}>
                  Create Appointment
                </button>
              </OverlayTrigger>
            </h3>
            <div
              id="upcomingAppointments"
              className="accordion-collapse collapse show p-3"
              aria-labelledby="upcomingAppointmentsHeader"
            >
              <UpcomingEventsTable
                upcomingEvents={dashboardQuery.data?.upcomingEvents}
                onUpdateAppointmentStatus={handleUpdateAppointmentStatus}
                onRescheduleAppointment={handleRescheduleAppointment}
                onClickUpcomingEvent={handleClickUpcomingAppointment}
              />
            </div>
          </div>
        </div>

        <AppointmentHistoryAccordion defaultActiveKey="0" appointmentHistory={dashboardQuery.data?.pastEvents} />

        {renderAppointmentFormModal()}
        {renderAppointmentSummaryModal()}
      </ContentPad>
    </>
  );
};
